<!-- ✅ UPDATED FILE: ForgotPassword.vue
  - Uses store timer as remaining resend time (already)
  - NEW: sync timer with backend retry_after on errors (429) and success
  - NEW: show "Resend in Xs" + disabled state by computed canResend
  - NEW: if backend says OTP expired/missing (422) on resend -> go back to step 1
-->
<template>
  <div class="min-h-screen bg-slate-50 dark:bg-slate-900" :dir="dir">
    <Navbar />

    <section class="container py-20 md:py-28">
      <!-- ✅ Centered wrapper -->
      <div class="flex justify-center">
        <div class="w-full max-w-lg">
          <div
            class="p-6 bg-white border shadow-sm rounded-2xl border-slate-200 dark:border-slate-700 dark:bg-slate-800 md:p-8"
          >
            <h1 class="mb-1 text-xl font-bold text-center text-slate-900 dark:text-white">
              {{ t("fp_title") }}
            </h1>
            <p class="mb-6 text-sm text-center text-slate-500 dark:text-slate-400">
              {{ t("fp_subtitle") }}
            </p>

            <!-- Step 1: Request Code -->
            <form v-if="step === 1" @submit.prevent="requestCode" class="grid grid-cols-1 gap-4">
              <BaseInput
                :label="t('fp_email_label')"
                v-model="form.identifier"
                :error="errors.identifier"
                type="email"
                :placeholder="t('fp_email_placeholder')"
              />

              <button
                type="submit"
                class="inline-flex items-center justify-center w-full h-10 px-6 text-sm font-semibold text-black rounded-md bg-secondary hover:bg-secondary/90 disabled:opacity-60"
                :disabled="loading"
              >
                <span v-if="!loading">{{ t("fp_send_code") }}</span>
                <span v-else class="inline-flex items-center gap-2">
                  <i class="text-xl mdi mdi-loading mdi-spin"></i> {{ t("fp_sending") }}
                </span>
              </button>

              <p class="text-sm text-center text-slate-600 dark:text-slate-300">
                {{ t("fp_remembered_password") }}
                <RouterLink to="/login" class="font-semibold text-primary hover:text-primary/90">
                  {{ t("fp_back_to_login") }}
                </RouterLink>
              </p>
            </form>

            <!-- Step 2: Verify Code -->
            <form v-else-if="step === 2" @submit.prevent="verifyCode" class="grid grid-cols-1 gap-4">
              <div>
                <label class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  {{ t("fp_otp_label") }}
                </label>

                <div class="flex justify-center gap-2" dir="ltr">
                  <input
                    v-for="(d, i) in otp"
                    :key="i"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    class="w-12 h-12 text-center bg-white border rounded-lg focus:outline-none focus:ring-2 border-primary/30 focus:ring-primary/40 dark:bg-slate-900 text-slate-900 dark:text-white"
                    dir="ltr"
                    style="unicode-bidi: plaintext"
                    v-model="otp[i]"
                    @input="onOtpInput(i, $event)"
                    @keydown.backspace.prevent="onOtpBackspace(i, $event)"
                    @paste.prevent="onOtpPaste($event)"
                    :ref="(el) => (otpRefs[i] = el)"
                  />
                </div>

                <!-- ✅ Resend button + Remaining time -->
                <div class="flex items-center justify-center gap-3 mt-3">
                  <button
                    type="button"
                    class="px-4 text-xs font-semibold rounded-md h-9 text-primary bg-secondary/30 hover:bg-secondary/40 disabled:opacity-50"
                    :disabled="!canResend || loading"
                    @click="resend"
                  >
                    <template v-if="timer > 0">
                      {{ t("fp_resend_in") }} <span class="tabular-nums">{{ timer }}</span>{{ t("fp_seconds") }}
                    </template>
                    <template v-else>
                      {{ t("fp_resend") }}
                    </template>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                class="inline-flex items-center justify-center w-full h-10 px-6 text-sm font-semibold text-black rounded-md bg-secondary hover:bg-secondary/90 disabled:opacity-60"
                :disabled="loading"
              >
                <span v-if="!loading">{{ t("fp_continue") }}</span>
                <span v-else class="inline-flex items-center gap-2">
                  <i class="text-xl mdi mdi-loading mdi-spin"></i> {{ t("fp_verifying") }}
                </span>
              </button>

              <p class="text-sm text-center text-slate-600 dark:text-slate-300">
                {{ t("fp_remembered_password") }}
                <RouterLink to="/login" class="font-semibold text-primary hover:text-primary/90">
                  {{ t("fp_back_to_login") }}
                </RouterLink>
              </p>
            </form>

            <!-- Step 3: Reset Password -->
            <form v-else @submit.prevent="resetPassword" class="grid grid-cols-1 gap-4">
              <BaseInput
                :label="t('fp_new_password')"
                v-model="form.password"
                :error="errors.password"
                type="password"
                :placeholder="t('fp_password_placeholder')"
              />
              <BaseInput
                :label="t('fp_confirm_password')"
                v-model="form.passwordConfirm"
                :error="errors.password_confirmation || errors.passwordConfirm"
                type="password"
                :placeholder="t('fp_password_placeholder')"
              />

              <p v-if="errors.message" class="-mt-1 text-xs text-red-600">{{ errors.message }}</p>

              <button
                type="submit"
                class="inline-flex items-center justify-center w-full h-10 px-6 text-sm font-semibold text-black rounded-md bg-secondary hover:bg-secondary/90 disabled:opacity-60"
                :disabled="loading"
              >
                <span v-if="!loading">{{ t("fp_set_password") }}</span>
                <span v-else class="inline-flex items-center gap-2">
                  <i class="text-xl mdi mdi-loading mdi-spin"></i> {{ t("fp_saving") }}
                </span>
              </button>

              <div class="flex items-center justify-center gap-3">
                <button
                  type="button"
                  class="px-4 text-xs font-semibold rounded-md h-9 text-primary bg-secondary/30 hover:bg-secondary/40"
                  :disabled="loading"
                  @click="backToCode"
                >
                  {{ t("fp_back_to_code") }}
                </button>
              </div>

              <p class="text-sm text-center text-slate-600 dark:text-slate-300">
                {{ t("fp_remembered_password") }}
                <RouterLink to="/login" class="font-semibold text-primary hover:text-primary/90">
                  {{ t("fp_back_to_login") }}
                </RouterLink>
              </p>
            </form>
          </div>
        </div>
      </div>

      <Toast
        :show="toast.show"
        :type="toast.type"
        :message="toast.message"
        @update:show="toast.show = $event"
      />
    </section>
  </div>
  <Footer />
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Navbar from "@/components/Navbar.vue";
import BaseInput from "@/components/BaseInput.vue";
import Footer from "@/components/Footer.vue";
import Toast from "@/components/Toast.vue";
import { useForgotPasswordStore } from "@/stores/forgotPasswordStore";
import { messages } from "@/i18n";

const router = useRouter();
const fp = useForgotPasswordStore();

/**
 * ✅ Reactive language (updates when <html lang=""> changes)
 */
const lang = ref(
  (document.documentElement.lang || localStorage.getItem("app_lang") || "ar")
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar"
);

function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

const dir = computed(() => (lang.value === "ar" ? "rtl" : "ltr"));

let _langObserver = null;

onMounted(() => {
  _langObserver = new MutationObserver(() => {
    const htmlLang = document.documentElement.lang || "ar";
    lang.value = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
  });

  _langObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang"],
  });
});

onBeforeUnmount(() => {
  if (_langObserver) _langObserver.disconnect();
});

// Keep these for UI (tabs commented, default email)
const method = ref("email"); // email | mobile
const step = ref(1);

const form = reactive({
  identifier: "",
  password: "",
  passwordConfirm: "",
});

const otp = ref(["", "", "", "", "", ""]);
const otpRefs = ref([]);

// bind store states
const loading = computed(() => fp.loading);
const errors = computed(() => fp.errors);
const timer = computed(() => fp.timer);
const canResend = computed(() => fp.canResend);

const toast = reactive({
  show: false,
  type: "error", // success | error | info
  message: "",
});

function showToast(message, type = "error") {
  toast.message = message;
  toast.type = type;
  toast.show = true;
}

function getFirstErrorMessage(errs) {
  if (!errs) return "";
  if (errs.message) return firstErr(errs.message);
  const keys = ["identifier", "code", "otp", "password", "passwordConfirm", "password_confirmation"];
  for (const key of keys) {
    if (errs[key]) return firstErr(errs[key]);
  }
  return "";
}

function firstErr(val) {
  return Array.isArray(val) ? val[0] : val;
}

function clearLocalErrors() {
  fp.clearErrors();
}

// keep store identifier synced if user changes
watch(
  () => form.identifier,
  (v) => {
    fp.identifier = v || "";
  }
);

function validateIdentifier() {
  clearLocalErrors();

  if (!form.identifier) {
    fp.errors = { ...fp.errors, identifier: t("fp_err_email_required") };
  } else if (!/^\S+@\S+\.\S+$/.test(form.identifier)) {
    fp.errors = { ...fp.errors, identifier: t("fp_err_email_invalid") };
  }

  const ok = Object.keys(fp.errors || {}).length === 0;
  if (!ok) {
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
  }
  return ok;
}

async function requestCode() {
  if (!validateIdentifier()) return;

  try {
    const res = await fp.requestCode({
      channel: method.value, // "email"
      identifier: form.identifier,
    });

    // ✅ if backend returns retry_after, store already starts timer
    step.value = 2;
    await nextTick();
    otpRefs.value?.[0]?.focus?.();
    showToast(res?.message || t("fp_toast_code_sent"), "success");
  } catch (e) {
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
  }
}

// OTP validation (client)
function validateOtpOnly() {
  clearLocalErrors();
  if (otp.value.join("").length !== 6) {
    fp.errors = { ...fp.errors, otp: t("fp_err_otp_invalid") };
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
    return false;
  }
  return true;
}

async function verifyCode() {
  if (!validateOtpOnly()) return;

  try {
    const res = await fp.verifyResetCode({
      code: otp.value.join(""),
      identifier: form.identifier,
    });

    step.value = 3;
    showToast(res?.message || t("fp_toast_code_verified"), "success");
  } catch (e) {
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
  }
}

// Password validation (client)
function validateReset() {
  clearLocalErrors();

  if (!form.password) fp.errors = { ...fp.errors, password: t("fp_err_password_required") };
  if (form.password && form.password.length < 8) fp.errors = { ...fp.errors, password: t("fp_err_password_min") };

  if (!form.passwordConfirm) fp.errors = { ...fp.errors, passwordConfirm: t("fp_err_confirm_required") };
  if (form.password && form.passwordConfirm && form.password !== form.passwordConfirm)
    fp.errors = { ...fp.errors, passwordConfirm: t("fp_err_password_mismatch") };

  const ok = Object.keys(fp.errors || {}).length === 0;
  if (!ok) {
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
  }
  return ok;
}

async function resetPassword() {
  if (!validateReset()) return;

  try {
    const res = await fp.resetPassword({
      identifier: form.identifier,
      token: fp.token,
      password: form.password,
      password_confirmation: form.passwordConfirm,
    });

    showToast(res?.message || t("fp_toast_password_set"), "success");
    router.push("/login");
  } catch (e) {
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
  }
}

async function backToCode() {
  step.value = 2;
  clearLocalErrors();
  await nextTick();
  otpRefs.value?.[0]?.focus?.();
}

async function resend() {
  try {
    const res = await fp.resend();

    // ✅ clear boxes and focus
    otp.value = ["", "", "", "", "", ""];
    await nextTick();
    otpRefs.value?.[0]?.focus?.();

    // ✅ timer updated in store using retry_after (success)
    showToast(res?.message || t("fp_toast_code_resent"), "success");
  } catch (e) {
    // If backend says missing/expired => return to step 1 to request a new code
    const status = e?.response?.status;
    if (status === 422) {
      step.value = 1;
    }

    // If cooldown (429), store already started timer from retry_after
    const msg = getFirstErrorMessage(fp.errors);
    if (msg) showToast(msg, "error");
  }
}

// OTP helpers
function onOtpInput(i, e) {
  const val = String(e?.target?.value ?? "").replace(/\D/g, "");
  otp.value[i] = val.slice(-1);
  if (val && otpRefs.value[i + 1]) otpRefs.value[i + 1]?.focus?.();
}

function onOtpBackspace(i) {
  if (!otp.value[i] && otpRefs.value[i - 1]) otpRefs.value[i - 1]?.focus?.();
  else otp.value[i] = "";
}

function onOtpPaste(e) {
  const paste = (e.clipboardData || window.clipboardData)?.getData("text")?.replace(/\D/g, "");
  if (!paste) return;

  const next = [...otp.value];
  for (let i = 0; i < Math.min(6, paste.length); i++) next[i] = paste[i];
  otp.value = next;

  nextTick(() => otpRefs.value?.[Math.min(paste.length, 5)]?.focus?.());
}
</script>
